import { useEffect } from 'react';

import { JasperactiveIntegration } from 'pages/partners/components/IntegrationsTab/components/JasperactiveIntegration/JasperactiveIntegration';
import { LTIApiIntegration } from 'pages/partners/components/IntegrationsTab/components/LTIApiIntegration/LTIApiIntegration';
import { BulbIntegration } from 'pages/partners/components/IntegrationsTab/components/BulbIntegration/BulbIntegration';
import { XEDIntegration } from 'pages/partners/components/IntegrationsTab/components/XEDIntegration/XEDIntegration';
import { ConsumerSettings, IntegrationVendor, PartnerApi, PartnerDetails } from 'apis/PartnerApi';
import { EIntegration } from 'pages/partners/components/IntegrationsTab/constants';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useNotification } from 'app/notifications';
import { Container } from 'react-bootstrap';
import { copyToClipboard } from 'utils';
import { BulbApi } from 'apis/BulbApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

import 'pages/partners/components/IntegrationsTab/styles/integrations-tab.scss';

interface IntegrationsTabProps {
    partnerDetails: PartnerDetails;
}

export const IntegrationsTab = ({ partnerDetails }: IntegrationsTabProps) => {

    const { formatMessage } = useIntl();
    const { catchWithToast, createToast } = useNotification();
    const [state, setState] = useSuperState({
        consumerSettings: undefined as ConsumerSettings | undefined,
        integrationVendors: [] as IntegrationVendor[],
        loadingData: true
    });
    const { consumerSettings, integrationVendors, loadingData } = state;

    useEffect(() => {

        getIntegrationVendors();

    }, []);

    const getIntegrationVendors = async () => {

        const integrationsInfo = await PartnerApi.GetIntegrationVendors(partnerDetails.ID, 1);

        for (let i = 0; i < integrationsInfo.length; i++) {

            const integration = integrationsInfo[i];

            switch (integration.VendorId) {

                case EIntegration.GMetrix: {

                    const LTIIntegration = await PartnerApi.GetConsumerSettingsByPartnerId(partnerDetails.ID).catch(catchWithToast(formatMessage({ id: 'failed_to_load_lti_integration_settings.b4bd660c', defaultMessage: 'Failed to load LTI integration settings.' })));
                    if (LTIIntegration) {

                        setState({ consumerSettings: LTIIntegration });

                        if (LTIIntegration.ConsumerKey && LTIIntegration.ConsumerKey.length > 0 && LTIIntegration.ConsumerSecret && LTIIntegration.ConsumerSecret.length > 0) {

                            integration.Key = LTIIntegration.ConsumerKey;
                            integration.Secret = LTIIntegration.ConsumerSecret;

                        } else {

                            integration.Key = '';
                            integration.Secret = '';

                        }

                    } else {

                        integration.Key = '';
                        integration.Secret = '';
                        integration.ErrorMessage = 'Something went wrong when getting your GMetrix LTI key and secret.';

                    }

                }
                    break;
                case EIntegration.Jasperactive: {

                    const jasperActiveIntegration = await PartnerApi.GetJasperActiveTeacherCodeByCenter(partnerDetails.ID);
                    if (jasperActiveIntegration.Done) {

                        integration.IntegrationId = jasperActiveIntegration.Message;

                    } else {

                        integration.ErrorMessage = formatMessage({ id: 'this_partner_has_a_jasperactive_order_bu.954629a7', defaultMessage: 'This partner has a Jasperactive order, but there was an error linking the GMetrix account with Jasperactive. Please contact us at support.gmetrix.net before making access codes using your order.' });

                    }

                }
                    break;
                case EIntegration.XED: {

                    const XEDIntegration = await PartnerApi.GetXEDIntegration(partnerDetails.ID);
                    if (XEDIntegration.Done) {

                        integration.IntegrationId = XEDIntegration.Message;

                    } else {

                        integration.ErrorMessage = 'Something went wrong when getting your GMetrix LTI key and secret.';

                    }

                }
                    break;
                case EIntegration.Bulb: {

                    const bulbIntegration = await BulbApi.GetGMetrixPartnerInfo(partnerDetails.ID);

                    if (bulbIntegration) {

                        integration.Key = bulbIntegration.ConsumerKey;
                        integration.Secret = bulbIntegration.ConsumerSecret;
                        integration.RedirectUrl = bulbIntegration.LaunchURL;

                    } else {

                        integration.Key = '';
                        integration.Secret = '';
                        integration.ErrorMessage = 'Something went wrong when getting your GMetrix LTI key and secret.';

                    }

                }
                    break;

            }

        }

        setState({ integrationVendors: integrationsInfo, loadingData: false });

    };

    const createConsumerSettings = async () => {

        const ltiApiIntegrationIndex = integrationVendors.findIndex(vendor => vendor.VendorId === EIntegration.GMetrix);
        if (ltiApiIntegrationIndex !== -1) {

            const settings = await PartnerApi.CreateConsumserSettingsByPartnerId(partnerDetails.ID).catch(catchWithToast(formatMessage({ id: 'failed_to_create_consumer_settings.618b3cd1', defaultMessage: 'Failed to create consumer settings.' })));
            if (settings && settings.ConsumerKey.length > 0 && settings.ConsumerSecret.length > 0) {

                const copyIntegrationVendors = [...integrationVendors];
                copyIntegrationVendors[ltiApiIntegrationIndex].Key = settings.ConsumerKey;
                copyIntegrationVendors[ltiApiIntegrationIndex].Secret = settings.ConsumerSecret;
                setState({ consumerSettings: settings, integrationVendors: copyIntegrationVendors });

            }

        }

    };

    const createBulbSettings = async () => {

        const ltiApiIntegrationIndex = integrationVendors.findIndex(vendor => vendor.VendorId === EIntegration.Bulb);
        const copyIntegrationVendors = [...integrationVendors];
        if (ltiApiIntegrationIndex !== -1) {

            const response = await BulbApi.Create(partnerDetails.Name).catch(catchWithToast(formatMessage({ id: 'failed_to_create_bulb_integration.08f5f158', defaultMessage: 'Failed to create Bulb integration.' })));
            if (response) {

                BulbApi.SetGMetrixPartnerInfo('bulb', response.ltiKey, response.ltiSecret, response.launchURI, partnerDetails.ID);

                copyIntegrationVendors[ltiApiIntegrationIndex].Key = response.ltiKey;
                copyIntegrationVendors[ltiApiIntegrationIndex].Secret = response.ltiSecret;
                copyIntegrationVendors[ltiApiIntegrationIndex].RedirectUrl = response.launchURI;

                setState({ integrationVendors: copyIntegrationVendors });

            }

        }

    };

    const copyClipboardWithMessage = (copyText: string) => {

        copyToClipboard(copyText);
        createToast(formatMessage({ id: 'successfully_copied_to_clipboard.c960a44f', defaultMessage: 'Successfully copied to clipboard' }), 'success');

    };

    const generateLTIKeyAndSecret = (integrationType: EIntegration) => {

        if (integrationType === EIntegration.GMetrix) {

            createConsumerSettings();

        } else {

            createBulbSettings();

        }

    };

    const resyncIntegrationClick = async (orderId: number, syncingIntegration: EIntegration) => {

        //Eventually we can remove this switch and won't need the baseUrl.includes on line 127. Sam wanted this ~ Spencer
        let baseUrl = window.location.host;

	    switch (true) {

	        case baseUrl.includes('devmanage') || baseUrl.includes('localhost'):
	            baseUrl = 'devmanagementservices';
	            break;
	        case baseUrl.includes('stage'):

	            // baseUrl = "stagemanagementservices";
                baseUrl = 'managementservices';
	            break;
	        case baseUrl.includes('preprod'):

	            // baseUrl = "preprodmanagementservices";
                baseUrl = 'managementservices';
	            break;
	        default:
	            baseUrl = 'managementservices';

	    }
        const copyIntegrationVendors = [...integrationVendors];
        const jasperactiveIntegrationIndex = copyIntegrationVendors.findIndex(integration => integration.VendorId === syncingIntegration);
        if (baseUrl.includes('devmanagementservices')) {

            copyIntegrationVendors[jasperactiveIntegrationIndex].ErrorMessage = "Management Services doesn't work on devmanage.";
            createToast(formatMessage({ id: 'management_services_doesnt_work_on_devma.c832f42c', defaultMessage: "Management Services doesn't work on devmanage." }), 'success');

        } else {

            const response = await PartnerApi.ReSyncIntegrationManagementServices(orderId);

            if (response.Successful) {

                copyIntegrationVendors[jasperactiveIntegrationIndex].ErrorMessage = '';
                copyIntegrationVendors[jasperactiveIntegrationIndex].IntegrationId = response.ExternalId;
                createToast(response.MsgSuccess, 'success');

            } else {

                copyIntegrationVendors[jasperactiveIntegrationIndex].ErrorMessage = response.MsgSuccess;
                createToast(response.MsgSuccess, 'danger');

            }

        }
        setState({ integrationVendors: copyIntegrationVendors });

    };

    return (
        <>
            <Container fluid className="integrations">

                {!loadingData ? (
                    <>

                        {
                            integrationVendors.map(integration => {

                                switch (integration.VendorId) {

                                    case EIntegration.GMetrix: {

                                        return (

                                            <LTIApiIntegration
                                                consumerSettings={consumerSettings}
                                                copyClipboardWithMessage={copyClipboardWithMessage}
                                                generateLTIKeyAndSecret={generateLTIKeyAndSecret}
                                                integration={integration}
                                                key={integration.VendorId}
                                            />
                                        );

                                    }
                                    case EIntegration.Jasperactive: {

                                        return (

                                            <JasperactiveIntegration
                                                copyClipboardWithMessage={copyClipboardWithMessage}
                                                integration={integration}
                                                key={integration.VendorId}
                                                resyncIntegrationClick={resyncIntegrationClick}
                                            />
                                        );

                                    }
                                    case EIntegration.XED: {

                                        return (

                                            <XEDIntegration
                                                copyClipboardWithMessage={copyClipboardWithMessage}
                                                integration={integration}
                                                key={integration.VendorId}
                                                resyncIntegrationClick={resyncIntegrationClick}
                                            />
                                        );

                                    }
                                    case EIntegration.Bulb: {

                                        return (

                                            <BulbIntegration
                                                copyClipboardWithMessage={copyClipboardWithMessage}
                                                generateLTIKeyAndSecret={generateLTIKeyAndSecret}
                                                integration={integration}
                                                key={integration.VendorId}
                                            />
                                        );

                                    }

                                }

                            })
                        }
                    </>
                )
                    : (
                        <Container className="d-flex justify-content-center py-4">
                            <GMetrixLoader/>
                        </Container>
                    )}

            </Container>
        </>
    );

};
